// Ressource du jour — une par JOUR OUVRABLE du challenge (14 → 25 septembre
// 2026). Contenu final validé par le client le 25.08.2026.
//
// PAS DE RESSOURCE LE WEEK-END : les 19 et 20 septembre sont volontairement
// absents (demande client — laisser les participants tranquilles le week-end).
// Aucun quiz non plus ces jours-là, et les notifications sont coupées
// (cf. supabase/functions/_shared/challengeDays.ts).
//
// Certaines entrées sont des APPS à installer, d'autres des SITES à consulter
// (`kind`) : le libellé affiché change en conséquence.
//
// ⚠️ Les ids sont STABLES : la table app_tests les référence, avec une
// contrainte UNIQUE (user_id, app_id). Un même id ne peut donc PAS servir deux
// jours différents — le 2ᵉ jour serait déjà « testé » et impossible à valider.
// D'où `geneveterroir` (17.09) et `geneveterroir-ambassadeurs` (22.09), qui
// pointent sur la même carte mais restent deux missions distinctes.

import { Platform } from 'react-native';

export interface TestApp {
  id: string;
  /** Nom propre (Yuka, Too Good To Go…) ou titre du site : NON traduit. */
  name: string;
  /** App à installer ou site à consulter — change le libellé affiché. */
  kind: 'app' | 'site';
  /** Clé i18n de la tagline ('apps.<id>.tagline'), rendue via t(app.taglineKey). */
  taglineKey: string;
  /** Lien par défaut (et unique pour les sites). */
  url: string;
  /** Fiches stores, quand l'app en a une par plateforme. */
  urlIos?: string;
  urlAndroid?: string;
  emoji: string;
  color: string;
  /** Jour du challenge ('YYYY-MM-DD'). Jamais un 19 ou 20 septembre. */
  challengeDate: string;
}

export const TEST_APPS: TestApp[] = [
  {
    id: 'myswissfood',
    name: 'MySwissFoodPyramid',
    kind: 'app',
    taglineKey: 'apps.myswissfood.tagline',
    // ⚠️ MySwissFoodPyramid est iOS UNIQUEMENT : le lien Play Store fourni par le
    // client (ch.blv.myfoodpyramid) renvoie 404 — c'est le bundle iOS recopié.
    // Vérifié le 25.08.2026 : l'app est introuvable sur le Play Store suisse,
    // par nom comme par éditeur. Android et repli pointent donc sur la page
    // officielle du BLV (déjà utilisée avant), qui porte le même contenu.
    url: 'https://www.blv.admin.ch/blv/fr/home/lebensmittel-und-ernaehrung/ernaehrung/empfehlungen-informationen/schweizer-ernaehrungsempfehlungen.html',
    urlIos: 'https://itunes.apple.com/ch/app/myswissfoodpyramid/id1386182432?l=de&ls=1&mt=8',
    urlAndroid: 'https://www.blv.admin.ch/blv/fr/home/lebensmittel-und-ernaehrung/ernaehrung/empfehlungen-informationen/schweizer-ernaehrungsempfehlungen.html',
    emoji: '🇨🇭',
    color: '#CD7728',
    challengeDate: '2026-09-14', // Lun. 14 — quiz protéines végétales
  },
  {
    id: 'vaisselle',
    name: 'La vaisselle réutilisable',
    kind: 'site',
    taglineKey: 'apps.vaisselle.tagline',
    url: 'https://www.ge.ch/dossier/reduisons-nos-dechets/reduire-nos-dechets-source/vaisselle-reutilisable-connait-refrain',
    emoji: '🍽️',
    color: '#1C453F',
    challengeDate: '2026-09-15', // Mar. 15 — quiz plastiques à usage unique
  },
  {
    id: 'frigomagic',
    name: 'Frigo Magic',
    kind: 'app',
    taglineKey: 'apps.frigomagic.tagline',
    url: 'https://play.google.com/store/apps/details?id=fr.haruni.frigomagic&hl=fr_CH',
    urlIos: 'https://apps.apple.com/ch/app/frigo-magic-cuisine-antigaspi/id977681072?l=fr-FR',
    urlAndroid: 'https://play.google.com/store/apps/details?id=fr.haruni.frigomagic&hl=fr_CH',
    emoji: '🧊',
    color: '#4A90E2',
    challengeDate: '2026-09-16', // Mer. 16 — quiz conservation des restes
  },
  {
    id: 'geneveterroir',
    name: 'Carte interactive du terroir genevois',
    kind: 'site',
    taglineKey: 'apps.geneveterroir.tagline',
    url: 'https://www.geneveterroir.ch/fr/carte',
    emoji: '🌾',
    color: '#D4A017',
    challengeDate: '2026-09-17', // Jeu. 17 — quiz GRTA
  },
  {
    id: 'poubelleverte',
    name: 'La P’tite Poubelle Verte',
    kind: 'site',
    taglineKey: 'apps.poubelleverte.tagline',
    url: 'https://www.ge.ch/teaser/p-tite-poubelle-verte',
    emoji: '♻️',
    color: '#2D7A3E',
    challengeDate: '2026-09-18', // Ven. 18 — quiz tri des déchets de cuisine
  },
  // 19 et 20 septembre : week-end, pas de ressource (ni de quiz).
  {
    id: 'toogoodtogo',
    name: 'Too Good To Go',
    kind: 'app',
    taglineKey: 'apps.toogoodtogo.tagline',
    url: 'https://www.toogoodtogo.com/',
    emoji: '🥡',
    color: '#1C453F',
    challengeDate: '2026-09-21', // Lun. 21 — quiz gaspillage en Suisse
  },
  {
    id: 'geneveterroir-ambassadeurs',
    name: 'Carte interactive du terroir genevois',
    kind: 'site',
    taglineKey: 'apps.geneveterroir.tagline',
    url: 'https://www.geneveterroir.ch/fr/carte',
    emoji: '🍷',
    color: '#B25B28',
    challengeDate: '2026-09-22', // Mar. 22 — quiz Ambassadeurs du terroir
  },
  {
    id: 'yuka',
    name: 'Yuka',
    kind: 'app',
    taglineKey: 'apps.yuka.tagline',
    url: 'https://yuka.io/',
    emoji: '🥑',
    color: '#83BF4F',
    challengeDate: '2026-09-23', // Mer. 23 — quiz ordre des ingrédients
  },
  {
    id: 'nosgestesclimat',
    name: 'Mon empreinte carbone',
    kind: 'site',
    taglineKey: 'apps.nosgestesclimat.tagline',
    url: 'https://geneve.nosgestesclimat.ch',
    emoji: '🌍',
    color: '#2D7A3E',
    challengeDate: '2026-09-24', // Jeu. 24 — quiz empreinte carbone
  },
  {
    id: 'calendriersaisons',
    name: 'Calendrier des saisons suisses',
    kind: 'site',
    taglineKey: 'apps.calendriersaisons.tagline',
    url: 'https://www.paysanssuisses.ch/calendrier-des-saisons',
    emoji: '📅',
    color: '#78A584',
    challengeDate: '2026-09-25', // Ven. 25 — quiz eau pour 1 kg de bœuf
  },
];

export function getTestAppForDate(challengeDate: string): TestApp | undefined {
  return TEST_APPS.find((a) => a.challengeDate === challengeDate);
}

/** Lien à ouvrir : fiche store de la plateforme si elle existe, sinon `url`. */
export function getTestAppUrl(app: TestApp): string {
  if (Platform.OS === 'ios' && app.urlIos) return app.urlIos;
  if (Platform.OS === 'android' && app.urlAndroid) return app.urlAndroid;
  return app.url;
}
